import { Button, Card, Checkbox, clsx, Input, showNotification } from 'design-system';
import { Form } from 'design-system/compat';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildRegisterLink } from '../../../lib/returnUrl';
import {
  getListOfCountries,
  setConfirmSignIn,
  setInformationAboutUser,
  setSubscription,
  setSubscriptionInvitationData,
  setUserId,
} from '../../../redux/actions';
import { fetch_request, request } from '../../../service/request';
import { initMlsPasswordBackup } from '../../../e2ee/mlsBackup.js';
import Header from '../CreateMembership/Header';
import ForgotPassword from './ForgotPassword';
import Invitation from './Invitation';
import loginStyles from './LoginForm.module.css';
import VerificationMessage from './VerificationMessage';

// Simple phone formatting: preserve leading + and group remaining digits for readability
const formatPhoneDisplay = (input, country) => {
  if (!input) return '';
  const raw = String(input);
  // Do not format emails
  if (/.+@.+\..+/.test(raw)) return raw;
  // Extract plus and digits
  const hasPlus = raw.trim().startsWith('+');
  const digits = raw.replace(/[^\d]/g, '');
  const dc = country?.dial_code ? String(country.dial_code).replace('+', '') : '';
  if (hasPlus) {
    if (dc && digits.startsWith(dc)) {
      const rest = digits.slice(dc.length);
      const grouped = rest.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
      return `+${dc}${grouped ? ' ' + grouped : ''}`;
    }
    // Unknown country code: group all digits in 3s
    const grouped = digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
    return `+${grouped}`;
  }
  // Local number: just group
  return digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
};

const LoginForm = ({ isJoinSheet = false, onAuthSuccess }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const intl = useIntl();
  const [form] = Form.useForm();
  const [hideLoginForm, setHideLoginForm] = useState(false);
  const [openEmailVerification, setOpenEmailVVerification] = useState(false);
  const [countries, setCountries] = useState([]);
  const [rememberMe, setRememberMe] = useState(() => {
    try {
      const stored = localStorage.getItem('rememberme');
      if (stored === null) return true;
      return stored === 'true' || stored === true;
    } catch {
      return true;
    }
  });
  const [loginError, setLoginError] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const nextParam = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return params.get('next');
  }, [location.search]);
  const registerLink = useMemo(
    () => buildRegisterLink(location, { returnUrlOverride: nextParam || undefined }),
    [location, nextParam],
  );

  const preferredCountry = useMemo(() => {
    if (!countries.length) return null;
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem('ng.preferredCountry') || 'null');
    } catch {}
    const bySaved = saved ? countries.find((c) => c?.id === saved?.id) : null;
    const lang =
      typeof navigator !== 'undefined' && navigator.language
        ? navigator.language.toLowerCase()
        : '';
    const langPrefix = lang ? lang.split('-')[0] : '';
    const localeDial =
      langPrefix === 'sv'
        ? '+46'
        : langPrefix === 'da'
          ? '+45'
          : langPrefix === 'fi'
            ? '+358'
            : langPrefix === 'nb' || langPrefix === 'nn' || langPrefix === 'no'
              ? '+47'
              : null;
    const byLocale = localeDial ? countries.find((c) => String(c?.dial_code) === localeDial) : null;
    return bySaved || byLocale || countries[0] || null;
  }, [countries]);

  const matchCountryFromDialCode = (value) => {
    if (!value || !value.startsWith('+') || !countries.length) return null;
    const normalized = value.replace(/\s+/g, '');
    const sorted = [...countries].sort(
      (a, b) => (b?.dial_code?.length || 0) - (a?.dial_code?.length || 0),
    );
    return sorted.find((c) => c?.dial_code && normalized.startsWith(c.dial_code)) || null;
  };

  const userInvitation = useSelector((state) => state.invitationToken.subscriptionInvitationData);

  useEffect(() => {
    fetch_request('/api/countries')
      .then((response) => response.json())
      .then((data) => {
        setCountries(data.countries);
        dispatch(getListOfCountries(data));
      })
      .catch((err) => console.log(err));
  }, [dispatch]);

  useEffect(() => {
    if (userInvitation?.email) {
      form.setFieldsValue({ name: userInvitation.email });
    }
  }, [userInvitation, form]);

  const buildLoginPayload = (formValues) => {
    const payload = {
      password: formValues.password,
      remember_me: rememberMe ? 1 : 0,
    };

    const rawInput = (formValues.name || '').trim();
    if (!rawInput) {
      return payload;
    }

    const valueNoSpaces = rawInput.replace(/\s+/g, '');
    payload.name = valueNoSpaces;

    const isEmail = /.+@.+\..+/.test(valueNoSpaces);
    if (isEmail) {
      payload.email = valueNoSpaces.toLowerCase();
      return payload;
    }

    let sanitized = valueNoSpaces.replace(/[^\d+]/g, '').replace(/(?!^)[+]/g, '');
    if (!/^\+?\d+$/.test(sanitized)) {
      showNotification(intl.formatMessage({ id: 'add_validNumber' }), {
        variant: 'error',
        context: 'auth',
        id: 'auth.invalid_phone',
      });
      return null;
    }

    let selectedCountry = null;
    let digitsOnly = sanitized.replace(/[+]/g, '');

    if (sanitized.startsWith('+')) {
      const matched = matchCountryFromDialCode(sanitized);
      if (matched) {
        selectedCountry = matched;
        const dialDigits = String(matched.dial_code || '').replace(/\D/g, '');
        if (dialDigits && digitsOnly.startsWith(dialDigits)) {
          digitsOnly = digitsOnly.slice(dialDigits.length);
        }
      }
    }

    if (!selectedCountry) {
      selectedCountry = preferredCountry || (countries.length ? countries[0] : null);
    }

    if (!selectedCountry || !digitsOnly) {
      showNotification(intl.formatMessage({ id: 'add_validNumber' }), {
        variant: 'error',
        context: 'auth',
        id: 'auth.invalid_phone',
      });
      return null;
    }

    payload.phone_number = digitsOnly;
    payload.country_id = selectedCountry.id;
    try {
      localStorage.setItem('ng.preferredCountry', JSON.stringify({ id: selectedCountry.id }));
    } catch {}

    return payload;
  };

  const attemptLogin = (payload) => {
    setOpenEmailVVerification(false);
    setLoginError(false);
    setLoginErrorMessage('');
    setIsSubmitting(true);

    return request('/sessions', payload, 'post')
      .then(async (response) => {
        if (response.data?.user) {
          const userId = response.data.user.id;
          const token = response.headers['x-authentication-token'];
          if (token) {
            localStorage.setItem('token', token);
          }
          localStorage.setItem('auth', 'true');
          dispatch(setUserId(userId));
          dispatch(setSubscriptionInvitationData({}));
          dispatch(setInformationAboutUser(response.data));
          dispatch(setSubscription(response.data.user.subscription));
          localStorage.setItem('user-slug', response.data.user.slug);
          localStorage.setItem('rememberme', rememberMe);

          if (rememberMe) {
            localStorage.setItem('id', response.data.user.id);
          } else {
            localStorage.removeItem('id');
          }
          await initMlsPasswordBackup({
            userId,
            password: payload.password,
          }).catch(() => false);
          // Reset anonymous gate counters after successful auth
          try {
            localStorage.removeItem('ng.gate.counts');
            localStorage.removeItem('ng.gate.ts');
            localStorage.removeItem('ng.gate.origin');
          } catch {}
          dispatch(setConfirmSignIn());
          setLoginError(false);

          if (typeof onAuthSuccess === 'function') {
            onAuthSuccess(response.data.user);
            return response;
          }

          // Redirect logic: next param > previousPath > (returning: Home/Anniversaries) or (first-time: Getting Started) > MyLight
          try {
            const params = new URLSearchParams(location.search || window.location.search);
            const nextUrl = params.get('next');
            const prev = sessionStorage.getItem('previousPath');
            const userId = response.data.user?.id ? String(response.data.user.id) : null;
            const skipKey = userId ? `ng.skipGettingStarted:${userId}` : null;
            const hasDismissedGettingStarted = skipKey
              ? localStorage.getItem(skipKey) === 'true'
              : false;
            const sparksHome = '/sparks';
            if (nextUrl) {
              navigate(nextUrl, { replace: true });
              return response;
            }
            if (prev) {
              sessionStorage.removeItem('previousPath');
              navigate(prev, { replace: true });
              return response;
            }
            // Decide between Home/Anniversaries (returning) vs Getting Started (first-time)
            fetch_request('/api/graves_joined_by_users')
              .then((r) => (r.ok ? r.json() : Promise.resolve({})))
              .then((json) => {
                const hasRelations = Array.isArray(json)
                  ? json.length > 0
                  : Array.isArray(json?.graves_joined_by_users)
                    ? json.graves_joined_by_users.length > 0
                    : false;
                if (!hasRelations && !hasDismissedGettingStarted) {
                  if (skipKey) {
                    localStorage.setItem(skipKey, 'true');
                  }
                  navigate('/getting-started', { replace: true });
                } else if (hasRelations) {
                  navigate(sparksHome, { replace: true });
                } else {
                  if (skipKey) {
                    localStorage.setItem(skipKey, 'true');
                  }
                  navigate(sparksHome, { replace: true });
                }
              })
              .catch(() => {
                // Fallback to Sparks hub
                navigate('/sparks', { replace: true });
              });
          } catch (e) {
            // Fallback
            navigate('/sparks', { replace: true });
          }
        }
        return response;
      })
      .catch((err) => {
        const status = err.response?.data?.status;
        if (status === 'unverified') {
          setOpenEmailVVerification(true);
          return Promise.reject(err);
        }

        const apiErrorKey =
          (typeof err.response?.data?.error === 'string' && err.response.data.error) ||
          'email_error_login';

        setLoginError(true);
        setLoginErrorMessage(apiErrorKey);
        return Promise.reject(err);
      })
      .finally(() => {
        submittingRef.current = false;
        setIsSubmitting(false);
      });
  };

  const onFinish = (values) => {
    if (submittingRef.current) return;

    const payload = buildLoginPayload(values);
    if (!payload) return;

    submittingRef.current = true;
    attemptLogin(payload).catch(() => {});
  };

  const resendVerificationEmail = () => {
    setOpenEmailVVerification(false);
    const raw = form.getFieldValue('name');
    const email = typeof raw === 'string' && /.+@.+\..+/.test(raw.trim()) ? raw.trim() : '';
    if (!email) return;
    const payload = {
      user: {
        email,
      },
    };
    request(`/account_confirmations/resend_account_confirmation_email`, payload, 'post')
      .then(() => {
        showNotification(
          intl.formatMessage({ id: 'sending_verification_email' }),
          'success',
          'auth',
        );
      })
      .catch((err) => {
        showNotification(err.response?.data?.error || err.message, 'error', 'auth');
      });
  };

  const emailOrPhone = Form.useWatch('name', form);
  const normalized = (emailOrPhone || '').trim().replace(/\s+/g, '');
  const looksLikeEmail = /.+@.+\..+/.test(normalized);
  const looksLikePhone = /^\+?\d[\d\s]*$/.test((emailOrPhone || '').trim());
  const inputMode = looksLikeEmail ? 'email' : looksLikePhone ? 'tel' : 'text';
  const apiErrorMessageKey = loginErrorMessage || 'email_error_login';
  const apiErrorMessage = loginError
    ? intl.formatMessage({ id: apiErrorMessageKey, defaultMessage: apiErrorMessageKey })
    : undefined;
  const emailFieldErrors = form.getFieldError('name');
  const passwordFieldErrors = form.getFieldError('password');
  const emailHasError = loginError || emailFieldErrors.length > 0;
  const passwordHasError = loginError || passwordFieldErrors.length > 0;
  const passwordHelp = passwordFieldErrors.length > 0 ? passwordFieldErrors[0] : apiErrorMessage;
  const emailRequiredMessage = intl.formatMessage({
    id: 'auth_emailOrPhone_required',
    defaultMessage: 'Email or phone number is required',
  });
  const passwordRequiredMessage = intl.formatMessage({
    id: 'account_form_password_required',
    defaultMessage: 'Password is required',
  });

  const handleFieldsChange = () => {
    if (loginError) {
      setLoginError(false);
      setLoginErrorMessage('');
    }
    if (openEmailVerification) setOpenEmailVVerification(false);
  };

  const formContent = !hideLoginForm ? (
    <>
      <Header messageKey="login_description" headingKey="auth_welcome" />
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        onFieldsChange={handleFieldsChange}
        className={loginStyles.form}
      >
        <div>
          <Form.Item
            name="name"
            label={
              <FormattedMessage
                id="auth_emailOrPhone_label"
                defaultMessage="Email or phone number"
              />
            }
            validateStatus={emailHasError ? 'error' : undefined}
            help={
              emailFieldErrors.length ? (
                <span id="login-identifier-error">{emailFieldErrors[0]}</span>
              ) : undefined
            }
            validateTrigger={['onBlur', 'onChange']}
            rules={[
              {
                required: true,
                message: emailRequiredMessage,
              },
              {
                validator: (_, value) => {
                  if (typeof value === 'string' && value.trim()) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(emailRequiredMessage));
                },
              },
            ]}
          >
            <Input
              className={clsx(loginStyles.textField, emailHasError && loginStyles.textFieldError)}
              onBlur={(e) => {
                const val = e?.target?.value ?? '';
                const trimmed = (val || '').trim();
                const phoneLike = /^\+?\d[\d\s]*$/.test(trimmed);
                if (phoneLike) {
                  const sanitized = trimmed.replace(/\s+/g, '');
                  const matched = matchCountryFromDialCode(sanitized) || preferredCountry;
                  const display = formatPhoneDisplay(val, matched);
                  form.setFieldsValue({ name: display });
                }
              }}
              placeholder={intl.formatMessage({
                id: 'auth_emailOrPhone_placeholder',
                defaultMessage: 'Enter email or phone number',
              })}
              color="default"
              inputMode={inputMode}
              name="username"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="next"
              aria-invalid={emailHasError}
              aria-describedby={emailFieldErrors.length ? 'login-identifier-error' : undefined}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="password"
          label={<FormattedMessage id="settings_password" defaultMessage="Password" />}
          validateStatus={passwordHasError ? 'error' : undefined}
          help={passwordHelp ? <span id="login-password-error">{passwordHelp}</span> : undefined}
          validateTrigger={['onBlur', 'onChange']}
          rules={[
            {
              required: true,
              message: passwordRequiredMessage,
            },
            {
              validator: (_, value) => {
                if (typeof value === 'string' && value.trim()) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(passwordRequiredMessage));
              },
            },
          ]}
        >
          <div
            className={clsx(
              loginStyles.passwordField,
              passwordHasError && loginStyles.passwordFieldError,
            )}
          >
            <Input
              className={clsx(
                loginStyles.textField,
                passwordHasError && loginStyles.textFieldError,
              )}
              type={showPassword ? 'text' : 'password'}
              placeholder={intl.formatMessage({
                id: 'enter_your_password',
              })}
              autoComplete="current-password"
              color="default"
              name="password"
              enterKeyHint="go"
              aria-invalid={passwordHasError}
              aria-describedby={passwordHasError ? 'login-password-error' : undefined}
            />
            <button
              type="button"
              className={loginStyles.passwordToggle}
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={intl.formatMessage({
                id: showPassword ? 'auth_hide_password' : 'auth_show_password',
                defaultMessage: showPassword ? 'Hide password' : 'Show password',
              })}
            >
              {!showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </Form.Item>
        {openEmailVerification && looksLikeEmail && (
          <VerificationMessage onResend={resendVerificationEmail} />
        )}
        {openEmailVerification && !looksLikeEmail && (
          <VerificationMessage
            messageId="login_phone_email_verification_message"
          />
        )}
        <Button
          variant="link"
          type="link"
          color="brand"
          className={loginStyles.forgotButton}
          onClick={() => setHideLoginForm(true)}
        >
          <FormattedMessage id="auth_forgotPassword" />
        </Button>

        <div className={loginStyles.checkboxGroup}>
          <Checkbox checked={rememberMe} onChange={() => setRememberMe(!rememberMe)}>
            <FormattedMessage id="auth_rememberMe" />
          </Checkbox>
        </div>

        <div className={loginStyles.actions}>
          <Button
            variant="filled"
            color="primary"
            block
            size="lg"
            htmlType="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            <FormattedMessage id="auth_login" />
          </Button>
        </div>
      </Form>
      {!isJoinSheet && (
        <div className={loginStyles.registerRow}>
          <span>
            <FormattedMessage id="auth_notRegistered" />
          </span>
          <Button
            variant="link"
            color="brand"
            onClick={() => navigate(registerLink)}
            className={loginStyles.registerLink}
            htmlType="button"
          >
            <FormattedMessage id="auth_register" />
          </Button>
        </div>
      )}
    </>
  ) : (
    <ForgotPassword setHideLoginForm={setHideLoginForm} />
  );

  return (
    <div
      className={loginStyles.container}
      style={{ minHeight: isJoinSheet ? 'auto' : 'calc(100vh - 120px)' }}
    >
      <Invitation />

      <div className={loginStyles.content}>
        <Card className={loginStyles.card}>{formContent}</Card>
      </div>
    </div>
  );
};

export default LoginForm;
